import React, {useState, useEffect, useMemo} from "react";
import Menu from "../../../../components/layout/menu/index";
import { useTable } from 'react-table';
import logoImage from "../../../../assets/images/ranking-foto-prueba.png"
import { useAuth } from "../../../../context/auth";
import { getRanking } from "../../../../client/ranking/index"
import { Link } from "react-router-dom";
import { getTopCriterion } from "../../../../client/criterio/index"
import { useModuleAdherence } from "../../../../hooks/userAdherence";
import icon1 from "../../../../assets/images/icons/ranking/criterion-icon-1.svg"
import icon2 from "../../../../assets/images/icons/ranking/criterion-icon-2.svg"
import icon3 from "../../../../assets/images/icons/ranking/criterion-icon-3.svg"
import { getUserData } from "../../../../client/users/index";
import ContentLoader from "react-content-loader";

export default function Ranking() {
    useModuleAdherence(2);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const {user, supplierId} = useAuth();
    const [title, setTitle] = useState("Ranking de Proveedores");

    const [top3, setTop3] = useState([]);
    const [tableData, setTableData] = useState([]);
    
    const [criteria, setCriteria] = useState([]);
    const [categoryId, setCategoryId] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateLoaded, setDataLoaded] = useState(false);

    const fixedImages = [icon1, icon2, icon3];

    useEffect(() => {
        const fetchAllData = async () => {
            if (!user?.id) {
                setError("Usuario no disponible");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                setDataLoaded(false);
                
                const userResponse = await getUserData(user.id);
                const userData = userResponse.data;
                
                if (!userData.categoryId) {
                    throw new Error("No se encontró categoryId en la respuesta del servidor");
                }
                
                const currentCategoryId = userData.categoryId;
                setCategoryId(currentCategoryId);
                
                const [criteriaResponse, rankingResponse] = await Promise.all([
                    supplierId ? getTopCriterion(supplierId.toString()).catch(err => {
                        console.error('Error fetching criteria:', err);
                        return { data: { top_criteria: [] } };
                    }) : Promise.resolve({ data: { top_criteria: [] } }),
                    getRanking(currentCategoryId.toString()).catch(err => {
                        console.error('Error fetching ranking:', err);
                        throw err;
                    })
                ]);
                
                if (criteriaResponse.data.top_criteria) {
                    const mappedCriteria = criteriaResponse.data.top_criteria.map(criterion => ({
                        id: criterion.criterion_name,
                        name: criterion.criterion_name,
                        weight: criterion.weight_criterion
                    }));
                    setCriteria(mappedCriteria);
                }
                
                setTitle(rankingResponse.data.message || "Ranking de Proveedores");
                const allData = rankingResponse.data.ranking || [];
                setTop3(allData.slice(0, 3)); 
                setTableData(allData.slice(3));
                
                setDataLoaded(true);
                
            } catch (error) {
                console.error('Error fetching data:', error);
                setError("Error al cargar la información del ranking");
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [user?.id, supplierId]);


    const data = useMemo(() => 
        tableData.map((supplier, index) => ({
        puesto: index + 4,
        empresa: supplier.supplierName,
        porcentaje: `${Math.round(supplier.finalScore)}%`,
        logo: logoImage
        })), 
    [tableData]);

    const columns = useMemo(() => [
        {
        Header: 'Puesto',
        accessor: 'puesto',
        style: { width: '60px' },
        },
        {
        Header: 'Empresa',
        accessor: 'empresa',
        style: { width: '200px' },
        Cell: ({ row }) => (
            <div className="flex items-center gap-3">
                <div className="w-[50px] aspect-square flex rounded-full items-center justify-center h-[50px] bg-[#D9D9D9] p-1">
                    <span className="text-white font-itcbold text-[20px]">
                    {getInitials(row.original.empresa)}
                    </span>
                </div>

            <span className="break-words text-[18px]">{row.original.empresa}</span>
            </div>
        )
        },
        {
        Header: 'Porcentaje',
        accessor: 'porcentaje',
        style: { width: '100px' },
        }
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data });

    const getInitials = (name) => {
    if (!name) return '--';
    const words = name.split(' ');
    return words
        .slice(0, 2)
        .map(word => word[0]?.toUpperCase() || '')
        .join('');
    };

    const TitleLoader = () => (
        <ContentLoader 
            speed={2}
            width={300}
            height={40}
            viewBox="0 0 300 40"
            backgroundColor="#e0e0e0"
            foregroundColor="#f5f5f5"
            className="mb-4 rounded-lg"
        >
            <rect x="0" y="0" rx="4" ry="4" width="350" height="40" />
        </ContentLoader>
    );

const CriterionCardLoader = () => (
    <ContentLoader 
        speed={2}
        width={300}
        height={100}
        viewBox="0 0 300 100"
        backgroundColor="#e0e0e0"
        foregroundColor="#f5f5f5"
        className="mb-4 px-1 min-w-[300px]"
    >
        <rect x="0" y="0" rx="8" ry="8" width="100%" height="100%" fill="white" />
        <rect x="0" y="0" rx="8" ry="8" width="100%" height="100%" className="shadow-[0px_-1px_4px_0px_#00000040,_0px_4px_4px_0px_#00000040]" />
        
        <rect x="20" y="20" rx="50" ry="50" width="60" height="60" />
        <rect x="100" y="25" rx="4" ry="4" width="150" height="20" />
        <rect x="100" y="55" rx="4" ry="4" width="80" height="30" />
    </ContentLoader>
);

const BenefitsBannerLoader = () => (
    <div className="w-full mb-4">
        <ContentLoader 
            speed={2}
            width={1200}
            height={60}
            viewBox="0 0 1200 60"
            backgroundColor="#e0e0e0"
            foregroundColor="#f5f5f5"
            className="w-full rounded-lg"
            preserveAspectRatio="none"
        >
            <rect x="0" y="0" rx="8" ry="8" width="100%" height="30" />
        </ContentLoader>
    </div>
);

    const TopCardLoader = ({ height = 346 }) => (
        <ContentLoader 
            speed={2}
            width={200}
            height={height}
            viewBox={`0 0 200 ${height}`}
            backgroundColor="#e0e0e0"
            foregroundColor="#f5f5f5"
        >
            <circle cx="100" cy="40" r="40" />
            <rect x="20" y="95" rx="4" ry="4" width="160" height="20" />
            <rect x="0" y="130" rx="8" ry="8" width="200" height={height - 140} />
            {height === 346 && <circle cx="100" cy="155" r="15" />}
            {height === 346 ? (
                <circle cx="100" cy="200" r="40" />
            ) : height === 297 ? (
                <circle cx="100" cy="180" r="30" />
            ) : (
                <circle cx="100" cy="160" r="25" />
            )}
            <rect x="70" y={height === 346 ? 260 : height === 297 ? 220 : 200} rx="4" ry="4" width="60" height="30" />
        </ContentLoader>
    );

const TableLoader = () => (
    <ContentLoader 
        speed={2}
        width="100%"
        height={300}
        viewBox="0 0 800 350"
        backgroundColor="#e0e0e0"
        foregroundColor="#f5f5f5"
        className="w-full"
        preserveAspectRatio="none"
    >
        <rect x="0" y="0" rx="4" ry="4" width="15%" height="40" />
        <rect x="20%" y="0" rx="4" ry="4" width="60%" height="40" />
        <rect x="85%" y="0" rx="4" ry="4" width="15%" height="40" />
        
        {[1, 2, 3, 4].map((row, index) => {
            const yPosition = 60 + (index * 68);
            return (
                <React.Fragment key={row}>
                    {/* Puesto  */}
                    <rect x="0%" y={yPosition} rx="4" ry="4" width="15%" height="25" />
                    
                    {/* Empresa  */}
                    <circle cx="22%" cy={yPosition + 15} r="10" />
                    <rect x="27%" y={yPosition} rx="4" ry="4" width="53%" height="25" />
                    
                    {/* Porcentaje */}
                    <rect x="85%" y={yPosition} rx="4" ry="4" width="15%" height="25" />
                </React.Fragment>
            );
        })}
    </ContentLoader>
);

    if (loading) {
        return (
            <div>
                <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300"/>
                <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
                    <div className="pt-4 sm:pt-8 pb-10 px-[4%] sm:px-[3%] flex flex-col gap-4">
                        {/* Título loader */}
                        <TitleLoader />
                        
                        {/* Tarjetas de criterios */}
                        <div className="w-full py-2 overflow-x-auto">
                            <div className="flex justify-around w-full gap-2 lg:gap-8">
                                <div className="px-1"><CriterionCardLoader /></div>
                                <div className="px-1"><CriterionCardLoader /></div>
                                <div className="px-1"><CriterionCardLoader /></div>
                            </div>
                        </div>
                        
                        {/* Banner de beneficios */}
                        <BenefitsBannerLoader />
                        
                        {/* Contenido principal  */}
                        <div className="flex flex-col items-start justify-between gap-6 pt-4 lg:gap-6 lg:flex-row">
                            {/* Top 3 Cards loader */}
                            <div className="flex items-end justify-between w-full gap-6 p-1 overflow-x-auto 2xl:justify-center 2xl:overflow-clip lg:w-1/2">
                                <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7">
                                    <TopCardLoader height={297} />
                                </div>
                                <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7">
                                    <TopCardLoader height={346} />
                                </div>
                                <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7">
                                    <TopCardLoader height={233} />
                                </div>
                            </div>
                            
                            {/* Tabla loader */}
                            <div className="w-full lg:w-1/2">
                                <div className="w-full max-h-[280px] bg-white rounded-[10px] shadow-[0px_4px_4px_0px_#00000040,_-1px_0px_4px_0px_#00000040]">
                                    <div className="w-full p-3">
                                        <TableLoader />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300"/>
            <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
                <div className="pt-4 sm:pt-8 pb-10 px-[4%] sm:px-[3%] flex flex-col gap-4">
                    <h1 className="text-blue font-itcbold md:text-[32px] text-2xl">
                        {title}
                    </h1>

                    <div className="w-full py-2 overflow-x-auto">
                        <div className="flex justify-around w-full gap-2 lg:gap-8">
                        {criteria.map((criterion, index) => (
                        <div key={criterion.id} className="px-1">
                            <div className="flex min-w-[300px]  items-center h-full gap-3 p-2 px-4 bg-white rounded-lg shadow-[0px_-1px_4px_0px_#00000040,_0px_4px_4px_0px_#00000040]">
                                <img 
                                    src={fixedImages[index]} 
                                    alt={criterion.name}
                                    className="w-full aspect-square max-w-[70px] sm:w-auto"
                                />
                                <div className="flex flex-col justify-between">
                                    <p className="font-itcbook text-[15px]">
                                    {criterion.name}
                                    </p>
                                    <p className="font-itcbold text-[40px]">
                                    {criterion.weight}%
                                    </p>
                                </div>
                            </div>
                        </div>
                        ))}
                        </div>
                    </div>
                    
                    <h2 className="w-full px-5 rounded-lg font-itcmedium text-sm md:text-[20px] bg-blue p-2 text-white">
                        ¿Quieres saber los beneficios ganados por su puntaje?. Ingresa{' '}
                        <Link to="/mis-beneficios" className="underline cursor-pointer ">
                        aquí
                        </Link>
                    </h2>
                    <div className="flex flex-col items-start justify-between gap-6 pt-4 lg:gap-6 lg:flex-row">
                        <div className={`flex items-end  ${
                            top3.length === 1 ? 'justify-center' : 
                            top3.length === 2 ? 'justify-between sm:justify-center gap-14' : 
                            'justify-between gap-6'
                            } w-full p-1 overflow-x-auto 2xl:justify-center 2xl:overflow-clip lg:w-1/2 `}>
                            {top3[1] && (
                            <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7 ">
                                <div className="relative flex justify-center w-full">
                                    <div className="w-full aspect-square shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] rounded-full  flex items-center justify-center">
                                        <div className="w-full aspect-square flex items-center justify-center border-[4px] border-[#D9D9D9] text-white bg-[#5CBDEB] rounded-full ">
                                            <p className="text-[42px] font-itcbold">{getInitials(top3[1]?.supplierName)}
                                            </p> 
                                        </div>
                                    </div>
                                    <div className="bg-[#203C84] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] h-[53px] w-full absolute right-0 left-0 -bottom-2 rounded-[10px] p-1 px-3 overflow-hidden">
                                        <p className="font-itcdemi text-center  text-[14px] md:text-[15px] break-words text-white line-clamp-2">
                                            {top3[1]?.supplierName}
                                        </p>
                                    </div>
                                </div>
                                <div className="h-[297px] gap-3 justify-start items-center flex flex-col bg-blue rounded-[10px] p-3 w-full">
                                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.0719 0.488805L40.6286 34.3025C40.4286 34.2882 40.2143 34.2882 40 34.2882C37.8302 34.2882 35.6914 34.5872 33.627 35.1469L5.68786 3.71561L8.98623 0.417377C9.27194 0.145953 9.65765 -0.0254723 10.0434 0.00309863C10.4434 0.0173841 10.8148 0.18881 11.0719 0.488805ZM0.414948 8.98866C-0.113614 9.51723 -0.142185 10.3744 0.357806 10.9458L25.1431 39.3739C25.6717 38.9596 26.2288 38.5596 26.8002 38.1882C28.0386 37.382 29.3466 36.7112 30.6969 36.1505L3.66447 5.73915L0.414948 8.98866ZM71.0138 0.417377C70.7281 0.145953 70.3423 -0.0111868 69.9424 0.00309863C69.5566 0.0173841 69.1709 0.203095 68.9138 0.50309L59.0711 11.9172L41.8285 31.3454L43.1428 32.8454L44.7142 34.7454C45.599 34.9174 46.4626 35.1535 47.3147 35.4228L74.3189 3.72247L71.0138 0.417377ZM79.5851 8.98866L76.3462 5.74986L50.1288 36.5269C51.169 37.0085 52.1899 37.5412 53.1712 38.1739C53.7426 38.5453 54.2998 38.931 54.8426 39.3596L79.6422 10.9458C80.1422 10.3744 80.1136 9.53151 79.5851 8.98866ZM60.6439 5.71729L65.5724 0.00309863H14.4439L19.4378 5.71729H60.6439ZM53.1456 14.2886L56.936 10.0162L58.1797 8.57438H21.9349L26.9288 14.2886H53.1456ZM49.9998 58.5735C49.9998 64.0876 45.5133 68.5734 40 68.5734C34.4867 68.5734 30.0002 64.0876 30.0002 58.5735C30.0002 53.0595 34.4867 48.5737 40 48.5737C45.5133 48.5737 49.9998 53.0595 49.9998 58.5735ZM51.6209 40.5766C48.8754 38.793 45.8398 37.6902 42.6061 37.2996C41.7801 37.1956 40.9277 37.1453 40 37.1453C35.8552 37.1453 31.8263 38.336 28.3498 40.5897C22.2269 44.5663 18.5718 51.2892 18.5718 58.5735C18.5718 70.3891 28.1838 80.0018 40 80.0018C51.8162 80.0018 61.4282 70.3891 61.4282 58.5735C61.4282 51.2648 57.7606 44.5356 51.6209 40.5766ZM40 71.4305C32.9103 71.4305 27.1431 65.6626 27.1431 58.5735C27.1431 51.4845 32.9103 45.7166 40 45.7166C47.0897 45.7166 52.8569 51.4845 52.8569 58.5735C52.8569 65.6626 47.0897 71.4305 40 71.4305Z" fill="white"/>
                                    <path d="M45.46 66H35.02V63.06C36.0333 62.4067 36.8867 61.8133 37.58 61.28C38.2733 60.7333 38.9267 60.1267 39.54 59.46C40.9 57.98 41.58 56.72 41.58 55.68C41.58 55.2533 41.4533 54.9067 41.2 54.64C40.9467 54.3733 40.6067 54.24 40.18 54.24C39.1267 54.24 38.56 55.02 38.48 56.58H34.88C34.92 55.1533 35.2133 54.02 35.76 53.18C36.24 52.4333 36.8867 51.8733 37.7 51.5C38.5133 51.1133 39.3867 50.92 40.32 50.92C41.3333 50.92 42.26 51.14 43.1 51.58C43.94 52.0067 44.5667 52.6533 44.98 53.52C45.2333 54.0933 45.36 54.66 45.36 55.22C45.36 55.98 45.22 56.72 44.94 57.44C44.66 58.16 44.24 58.86 43.68 59.54C43.1333 60.22 42.6333 60.78 42.18 61.22C41.74 61.66 41.16 62.1933 40.44 62.82H45.46V66Z" fill="#007696"/>
                                    </svg>

                                    <p className="text-6xl text-white font-itcbold">
                                        {Math.round(top3[1]?.finalScore)}%
                                    </p>
                                </div>
                            </div>
                            )}

                            {top3[0] && (
                            <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7 ">
                                <div className="relative flex justify-center w-full">
                                    <div className="w-full aspect-square shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] rounded-full  flex items-center justify-center">
                                        <div className="w-full aspect-square flex items-center justify-center border-[4px] border-[#D9D9D9] text-white bg-[#5CBDEB] rounded-full ">
                                            <p className="text-[42px] font-itcbold">
                                                {getInitials(top3[0]?.supplierName)}
                                            </p> 
                                        </div>
                                    </div>
                                    <div className="bg-[#203C84] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] h-[53px] w-full absolute right-0 left-0 -bottom-2 rounded-[10px] p-1 px-3 overflow-hidden">
                                        <p className="font-itcdemi text-center text-[14px] md:text-[15px] break-words text-white line-clamp-2">
                                            {top3[0]?.supplierName}
                                        </p>
                                    </div>
                                </div>
                                <div className="h-[346px] gap-3 justify-start items-center flex flex-col bg-blue rounded-[10px] p-3 w-full">
                                    <p className="p-2 bg-white rounded-full text-blue font-itcbold text-[20px] w-[29px] h-[28px] flex items-center justify-center">
                                        1
                                    </p>
                                    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M85.9375 18.75H73.4375V12.5C75.1634 12.5 76.5625 11.1009 76.5625 9.375C76.5625 7.64906 75.1634 6.25 73.4375 6.25H26.5625C24.8366 6.25 23.4375 7.64906 23.4375 9.375C23.4375 11.1009 24.8366 12.5 26.5625 12.5V18.75H14.0625C12.3438 18.75 10.9375 20.1562 10.9375 21.875V25C10.9375 36.0156 18.4375 45.625 29.125 48.2969L30.2656 48.5781H30.2812C33.6089 53.7806 38.952 57.577 45.2164 58.89C44.9183 63.362 43.8723 67.7531 42.1081 71.875H34.375C32.6562 71.875 31.25 73.2812 31.25 75V87.5C29.5241 87.5 28.125 88.8991 28.125 90.625V93.75H71.875V90.625C71.875 88.8991 70.4759 87.5 68.75 87.5V75C68.75 73.2812 67.3438 71.875 65.625 71.875H57.8919C56.1278 67.7531 55.0817 63.362 54.7836 58.89C61.048 57.577 66.3911 53.7806 69.7188 48.5781H69.7344L70.875 48.2969C81.5625 45.625 89.0625 36.0156 89.0625 25V21.875C89.0625 20.1562 87.6562 18.75 85.9375 18.75ZM57.8125 79.6875C57.8125 80.5469 57.1094 81.25 56.25 81.25H43.75C42.8906 81.25 42.1875 80.5469 42.1875 79.6875C42.1875 78.8281 42.8906 78.125 43.75 78.125H56.25C57.1094 78.125 57.8125 78.8281 57.8125 79.6875ZM17.1875 25H26.5625V35.9375C26.5625 37.6562 26.75 39.3125 27.0938 40.9219C21.125 38 17.1875 31.875 17.1875 25ZM55.7969 41.7344L50 38.6875L44.2031 41.7344L45.3125 35.2812L40.625 30.7031L47.1094 29.7656L50 23.8906L52.8906 29.7656L59.375 30.7031L54.6875 35.2812L55.7969 41.7344ZM72.9062 40.9219C73.25 39.3125 73.4375 37.6562 73.4375 35.9375V25H82.8125C82.8125 31.875 78.875 38 72.9062 40.9219Z" fill="white"/>
                                    </svg>
                                    <p className="text-6xl text-white font-itcbold">
                                        {Math.round(top3[0]?.finalScore)}% 
                                    </p>
                                </div>
                            </div>
                            )}

                            {top3[2] && (
                            <div className="flex flex-col items-center justify-end min-w-[160px] lg:min-w-max gap-7 ">
                                <div className="relative flex justify-center w-full">
                                    <div className="w-full aspect-square shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] rounded-full  flex items-center justify-center">
                                        <div className="w-full aspect-square flex items-center justify-center border-[4px] border-[#D9D9D9] text-white bg-[#5CBDEB] rounded-full ">
                                            <p className="text-[42px] font-itcbold">
                                                {getInitials(top3[2]?.supplierName)}
                                            </p> 
                                        </div>
                                    </div>
                                    <div className="bg-[#203C84] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),-1px_0px_4px_0px_rgba(0,0,0,0.25)] h-[53px] w-full absolute right-0 left-0 -bottom-2 rounded-[10px] p-1 px-3 overflow-hidden">
                                        <p className="font-itcdemi text-center text-[14px] md:text-[15px] break-words text-white line-clamp-2">
                                            {top3[2]?.supplierName }
                                        </p>
                                    </div>
                                </div>
                                <div className="h-[233px] gap-3 justify-start items-center flex flex-col bg-blue rounded-[10px] p-3 w-full">
                                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.0719 0.488805L40.6286 34.3025C40.4286 34.2882 40.2143 34.2882 40 34.2882C37.8302 34.2882 35.6914 34.5872 33.627 35.1469L5.68786 3.71561L8.98623 0.417377C9.27194 0.145953 9.65765 -0.0254723 10.0434 0.00309863C10.4434 0.0173841 10.8148 0.18881 11.0719 0.488805ZM0.414948 8.98866C-0.113614 9.51723 -0.142185 10.3744 0.357806 10.9458L25.1431 39.3739C25.6717 38.9596 26.2288 38.5596 26.8002 38.1882C28.0386 37.382 29.3466 36.7112 30.6969 36.1505L3.66447 5.73915L0.414948 8.98866ZM71.0138 0.417377C70.7281 0.145953 70.3423 -0.0111868 69.9424 0.00309863C69.5566 0.0173841 69.1709 0.203095 68.9138 0.50309L59.0711 11.9172L41.8285 31.3454L43.1428 32.8454L44.7142 34.7454C45.599 34.9174 46.4626 35.1535 47.3147 35.4228L74.3189 3.72247L71.0138 0.417377ZM79.5851 8.98866L76.3462 5.74986L50.1288 36.5269C51.169 37.0085 52.1899 37.5412 53.1712 38.1739C53.7426 38.5453 54.2998 38.931 54.8426 39.3596L79.6422 10.9458C80.1422 10.3744 80.1136 9.53151 79.5851 8.98866ZM60.6439 5.71729L65.5724 0.00309863H14.4439L19.4378 5.71729H60.6439ZM53.1456 14.2886L56.936 10.0162L58.1797 8.57438H21.9349L26.9288 14.2886H53.1456ZM49.9998 58.5735C49.9998 64.0876 45.5133 68.5734 40 68.5734C34.4867 68.5734 30.0002 64.0876 30.0002 58.5735C30.0002 53.0595 34.4867 48.5737 40 48.5737C45.5133 48.5737 49.9998 53.0595 49.9998 58.5735ZM51.6209 40.5766C48.8754 38.793 45.8398 37.6902 42.6061 37.2996C41.7801 37.1956 40.9277 37.1453 40 37.1453C35.8552 37.1453 31.8263 38.336 28.3498 40.5897C22.2269 44.5663 18.5718 51.2892 18.5718 58.5735C18.5718 70.3891 28.1838 80.0018 40 80.0018C51.8162 80.0018 61.4282 70.3891 61.4282 58.5735C61.4282 51.2648 57.7606 44.5356 51.6209 40.5766ZM40 71.4305C32.9103 71.4305 27.1431 65.6626 27.1431 58.5735C27.1431 51.4845 32.9103 45.7166 40 45.7166C47.0897 45.7166 52.8569 51.4845 52.8569 58.5735C52.8569 65.6626 47.0897 71.4305 40 71.4305Z" fill="white"/>
                                    <path d="M45.58 61.64C45.58 63 45.0133 64.1133 43.88 64.98C42.76 65.8467 41.5 66.28 40.1 66.28C39.1933 66.28 38.3067 66.1067 37.44 65.76C36.5867 65.4 35.9333 64.88 35.48 64.2C35.0533 63.5467 34.78 62.6733 34.66 61.58H38.48C38.68 62.5 39.2533 62.96 40.2 62.96C40.7067 62.96 41.1 62.8133 41.38 62.52C41.66 62.2133 41.8 61.8067 41.8 61.3C41.8 61.0867 41.7667 60.9 41.7 60.74C41.6333 60.5667 41.5533 60.4267 41.46 60.32C41.3667 60.2133 41.2333 60.1267 41.06 60.06C40.8867 59.98 40.7267 59.9267 40.58 59.9C40.4467 59.86 40.2667 59.8333 40.04 59.82C39.8133 59.7933 39.6333 59.78 39.5 59.78C39.3667 59.78 39.18 59.7867 38.94 59.8C38.7133 59.8 38.56 59.8 38.48 59.8V57.02C38.8 57.02 39.0267 57.02 39.16 57.02C39.3067 57.0067 39.52 56.98 39.8 56.94C40.0933 56.8867 40.3 56.8133 40.42 56.72C40.5533 56.6267 40.68 56.48 40.8 56.28C40.92 56.08 40.98 55.8333 40.98 55.54C40.98 55.18 40.8933 54.8733 40.72 54.62C40.5467 54.3667 40.2867 54.24 39.94 54.24C39.1933 54.24 38.8067 54.6667 38.78 55.52H35.06C35.0733 54.0933 35.56 52.9733 36.52 52.16C37.48 51.3333 38.68 50.92 40.12 50.92C41.36 50.92 42.44 51.2933 43.36 52.04C44.2933 52.7733 44.76 53.7467 44.76 54.96C44.76 56.32 44.1067 57.3533 42.8 58.06C44.6533 58.6867 45.58 59.88 45.58 61.64Z" fill="#007696"/>
                                    </svg>
                                    <p className="text-6xl text-white font-itcbold">
                                        {Math.round(top3[2]?.finalScore)}%
                                    </p>
                                </div>
                            </div>
                            )}

                        </div>

                        <div className="w-full lg:w-1/2">
                            <div className="w-full h-full bg-white rounded-[10px] shadow-[0px_4px_4px_0px_#00000040,_-1px_0px_4px_0px_#00000040]">
                                <div className="w-full p-3 overflow-x-auto">
                                    {tableData.length > 0 ? (
                                    <table {...getTableProps()} className="w-full table-fixed">
                                        <thead>
                                            {headerGroups.map(headerGroup => (
                                            <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-[#A99E9E]">
                                                {headerGroup.headers.map((column, columnIndex) => (
                                                <th 
                                                    {...column.getHeaderProps()} 
                                                    style={column.style}
                                                    className={`px-2 py-2 font-itcbold text-blue text-[15px]
                                                        ${columnIndex === 0 ? 'text-center' : ''}
                                                        ${columnIndex === 1 ? 'text-left' : ''}
                                                `}
                                                >
                                                    {column.render('Header')}
                                                </th>
                                                ))}
                                            </tr>
                                            ))}
                                        </thead>
                                        <tbody {...getTableBodyProps()}>
                                            {rows.map(row => {
                                            prepareRow(row);
                                                return (
                                                    <tr {...row.getRowProps()} className="border-b h-[75px] border-[#A99E9E]">
                                                    {row.cells.map((cell, cellIndex) => {
                                                        const isFirstColumn = cellIndex === 0;
                                                        const isSecondColumn = cellIndex === 1;
                                                        const isThirdColumn = cellIndex === 2;                                        return (
                                                        <td 
                                                            {...cell.getCellProps()} 
                                                            className={`px-2 py-3 h-full text-[20px]
                                                                ${isFirstColumn ? 'text-center' : ''}
                                                                ${isSecondColumn ? 'text-left ' : ''}
                                                                ${isThirdColumn ? 'text-blue font-itcbold text-center' : 'font-itcbook'}
                                                            `}
                                                        >
                                                            {cell.render('Cell')}
                                                        </td>
                                                        );
                                                    })}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    ) : (
                                        <div className="w-full py-10 text-center">
                                            <p className="text-lg text-gray-500 font-itcmedium">
                                                No hay más registros para este ranking.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}